import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { AuthProvider } from '../contexts/AuthContext';
import { PageLoader } from '../components/common/PageLoader';
import { ErrorBoundary } from '../components/common/ErrorBoundary';
import { getPublicUrl } from '../utils/domain';

// Eagerly loaded (frequently used, lightweight)
import Index from '../pages/Index';
import Auth from '../pages/Auth';
import Search from '../pages/Search';
import Welcome from '../pages/Welcome';
import Activate from '../pages/Activate';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import CompanyOwnerRoute from '../components/auth/CompanyOwnerRoute';

// Lazy loaded - User pages
const ResetPassword = lazy(() => import('../pages/ResetPassword'));
const Memorials = lazy(() => import('../pages/Memorials'));
const Dashboard = lazy(() => import('../pages/Dashboard'));
const EditMemorial = lazy(() => import('../pages/EditMemorial'));
const Memorial = lazy(() => import('../pages/Memorial'));
const Account = lazy(() => import('../pages/Account'));
const CancelOrder = lazy(() => import('../pages/CancelOrder'));

// Statically imported by Auth.tsx, so we import it the same way for consistency
import MemorialCreationFlow from '../components/MemorialCreationFlow';

// Lazy loaded - Landing pages and legal
const LeSueurLanding = lazy(() => import('../pages/LeSueurLanding'));
const WrightLanding = lazy(() => import('../pages/WrightLanding'));
const TermsOfService = lazy(() => import('../pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('../pages/PrivacyPolicy'));

// Lazy loaded - Super Admin
const SuperAdminDashboard = lazy(() => import('../pages/SuperAdminDashboard'));

// Portal pages — eager imports so tab switching is instant (no Suspense flash)
const PortalAuth = lazy(() => import('../pages/portal/PortalAuth'));
import PortalDashboard from '../pages/portal/PortalDashboard';
import PortalMemorials from '../pages/portal/PortalMemorials';
import PortalTeam from '../pages/portal/PortalTeam';
import PortalUploadPhoto from '../pages/portal/PortalUploadPhoto';
import PortalSettings from '../pages/portal/PortalSettings';
import PortalEarnings from '../pages/portal/PortalEarnings';
import PortalFixRequests from '../pages/portal/PortalFixRequests';
import PortalInventory from '../pages/portal/PortalInventory';
import PortalRates from '../pages/portal/PortalRates';
import PortalTrainingVideos from '../pages/portal/PortalTrainingVideos';
import PortalProtectedRoute from '../components/portal/PortalProtectedRoute';
import PortalLayout from '../components/portal/PortalLayout';

// Partner pages — eager imports so tab switching is instant (no Suspense flash)
const PartnerAuth = lazy(() => import('../pages/partner/PartnerAuth'));
import PartnerDashboard from '../pages/partner/PartnerDashboard';
import PartnerOrders from '../pages/partner/PartnerOrders';
import PartnerPricing from '../pages/partner/PartnerPricing';
import PartnerEarnings from '../pages/partner/PartnerEarnings';
import PartnerTeam from '../pages/partner/PartnerTeam';
import PartnerSettings from '../pages/partner/PartnerSettings';
import PartnerProtectedRoute from '../components/partner/PartnerProtectedRoute';
import PartnerLayout from '../components/partner/PartnerLayout';

/**
 * Redirects /memorial/:id to public domain for SEO and existing QR codes.
 *
 * Exception: the setup-flow preview iframes its own /memorial/:id from the
 * app domain — the cross-origin redirect to pontis.life is exactly what the
 * parent's CSP frame-src blocks. Render the Memorial directly when framed.
 */
const RedirectMemorialToPublic = () => {
  const { id } = useParams();
  const location = useLocation();

  if (window.self !== window.top) {
    return <Memorial />;
  }

  const fullPath = `/memorial/${id}${location.search}${location.hash}`;
  window.location.href = getPublicUrl(fullPath);
  return null;
};

/**
 * App Router for app.pontis.life domain
 * Serves authenticated app pages with full functionality
 */
const AppRouter = () => {
  return (
    <AuthProvider>
      <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Redirect memorial routes to public domain */}
        <Route path="/memorial/:id" element={<RedirectMemorialToPublic />} />
        
        {/* Public pages (also available on app domain for convenience) */}
        <Route path="/" element={<Index />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/search" element={<Search />} />
        <Route path="/cancel-order" element={<CancelOrder />} />
        <Route path="/LeSueur" element={<LeSueurLanding />} />
        <Route path="/Wright" element={<WrightLanding />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        
        {/* Welcome and activation flows */}
        <Route path="/welcome/:id" element={<Welcome />} />
        <Route path="/activate/:id" element={<Activate />} />
        
        {/* Protected user routes */}
        <Route path="/memorials" element={
          <ProtectedRoute>
            <Memorials />
          </ProtectedRoute>
        } />
        
        <Route path="/create-memorial" element={
          <ProtectedRoute>
            <MemorialCreationFlow />
          </ProtectedRoute>
        } />
        
        <Route path="/edit-memorial" element={
          <ProtectedRoute>
            <EditMemorial />
          </ProtectedRoute>
        } />
        
        <Route path="/admin" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        
        <Route path="/account" element={
          <ProtectedRoute>
            <Account />
          </ProtectedRoute>
        } />
        
        {/* Super Admin */}
        <Route path="/super-admin" element={
          <ProtectedRoute>
            <CompanyOwnerRoute>
              <SuperAdminDashboard />
            </CompanyOwnerRoute>
          </ProtectedRoute>
        } />
        
        {/* Monument Company Portal Routes */}
        <Route path="/portal/auth" element={<PortalAuth />} />
        <Route path="/portal" element={
          <PortalProtectedRoute>
            <PortalLayout />
          </PortalProtectedRoute>
        }>
          <Route index element={<Navigate to="/portal/dashboard" replace />} />
          <Route path="dashboard" element={<PortalDashboard />} />
          <Route path="upload-photo" element={<PortalUploadPhoto />} />
          <Route path="memorials" element={<PortalMemorials />} />
          <Route path="inventory" element={<PortalInventory />} />
          <Route path="earnings" element={<PortalEarnings />} />
          <Route path="rates" element={<PortalRates />} />
          <Route path="fix-requests" element={<PortalFixRequests />} />
          <Route path="training-videos" element={<PortalTrainingVideos />} />
          <Route path="team" element={<PortalTeam />} />
          <Route path="settings" element={<PortalSettings />} />
        </Route>

        {/* Fulfillment Partner Portal Routes */}
        <Route path="/partner/auth" element={<PartnerAuth />} />
        <Route path="/partner" element={
          <PartnerProtectedRoute>
            <PartnerLayout />
          </PartnerProtectedRoute>
        }>
          <Route index element={<Navigate to="/partner/dashboard" replace />} />
          <Route path="dashboard" element={<PartnerDashboard />} />
          <Route path="orders" element={<PartnerOrders />} />
          <Route path="pricing" element={<PartnerPricing />} />
          <Route path="earnings" element={<PartnerEarnings />} />
          <Route path="team" element={<PartnerTeam />} />
          <Route path="settings" element={<PartnerSettings />} />
        </Route>
      </Routes>
      </Suspense>
      </ErrorBoundary>
    </AuthProvider>
  );
};

export default AppRouter;
